import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, UserMinus, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtShortDate } from "@/lib/format";
import { Avatar, ErrorBox, Spinner } from "@/components/Bits";
import ConfirmButton from "@/components/ConfirmButton";

export default function MembersTab({ league, owner, reload }) {
  const { user, tz, loadLeagues } = useAuth();
  const [members, setMembers] = useState(null);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const load = useCallback(() => { api.get(`/leagues/${league.id}/members`).then(({ data }) => setMembers(data)).catch((e) => setError(errMsg(e))); }, [league.id]);
  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    try {
      await api.delete(`/leagues/${league.id}/members/${m.id}`);
      toast.success(`${m.display_name} a été retiré de la ligue`);
      load();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const leave = async () => {
    try {
      await api.post(`/leagues/${league.id}/leave`);
      toast.success("Vous avez quitté la ligue");
      await loadLeagues();
      nav("/ligues");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (error) return <ErrorBox message={error} />;
  if (!members) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="members-tab">
      <div className="mpl-card divide-y divide-white/[0.05]">
        {members.map((m) => (
          <div key={m.id} data-testid={`member-row-${m.id}`} className="flex items-center gap-3 px-4 py-3">
            <Avatar user={m} size={38} />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 truncate font-semibold">{m.display_name} {m.role === "owner" && <Crown size={13} className="text-[#B7F52A]" />} {m.id === user.id && <span className="text-xs text-[#B7F52A]">(vous)</span>}</p>
              {m.username && <p className="truncate text-xs font-semibold text-[#B7F52A]" data-testid={`member-username-${m.id}`}>@{m.username}</p>}
              <p className="text-xs text-slate-500">Membre depuis le {fmtShortDate(m.joined_at, tz)}</p>
            </div>
            {m.id !== user.id && m.avatar?.startsWith("img:") && (
              <button onClick={() => api.post(`/avatars/${m.id}/report`, { reason: "" }).then(() => toast.success("Photo signalée à l'administration")).catch((e) => toast.error(errMsg(e)))}
                aria-label={`Signaler la photo de ${m.display_name}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 hover:text-amber-300" data-testid={`report-avatar-${m.id}`}><Flag size={14} /></button>
            )}
            {owner && m.id !== user.id && (
              <ConfirmButton label={<UserMinus size={16} />} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 hover:text-red-300"
                testId={`remove-member-${m.id}`} title={`Retirer ${m.display_name} ?`} confirmLabel="Retirer"
                description="Ce membre perdra l'accès à la ligue. Ses résultats passés restent dans l'historique." onConfirm={() => remove(m)} />
            )}
          </div>
        ))}
      </div>
      {!owner && !league.observer && (
        <ConfirmButton label="Quitter la ligue" testId="leave-league-button" title="Quitter cette ligue ?" confirmLabel="Quitter"
          description="Vous n'apparaîtrez plus au classement. L'historique des résultats est conservé." onConfirm={leave} />
      )}
    </div>
  );
}
